import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import qs from 'qs';

import { FLOORPLAN_VIEW, RESOURCES_VIEW_QUERY_PARAM } from 'floorplan/pages/native-floorplan/const';
import { useDefaultFloorplanDate } from './use-default-floorplan-date.hook';
import { useFloorplanViewState } from './use-floorplan-view-state.hook';
import { useActiveFloorplan } from './use-active-floorplan.hook';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { replace } from 'store/router/actions';
import { searchParams as SearchParams } from 'shared/types/search-params';
import { Floorplan } from 'floorplan/pages/native-floorplan/types';

interface UseResourcesViewValues {
  /** The toggle is only offered when there is something to toggle to. */
  isToggleVisible: boolean;
  isFloorplanView: boolean;
  floorplans: Floorplan[];
  /** Null unless the map is shown. */
  activeFloorplan: Floorplan | null;
  onFloorplanSelect: (floorplanUuid: string) => void;
  showList: VoidFunction;
  showFloorplan: VoidFunction;
}

/**
 * Which view the resources page is showing — the resource list or the floorplan map.
 *
 * The list stays the default (confirmed against the prototype, whose `mobView` starts as `list`), so
 * the floorplan is an opt-in the url records. Keeping it in the url means the back button, a reload
 * and a shared link all land on the view the user was looking at, with the same date and filters.
 *
 * The toggle appears only with the flag on *and* at least one floorplan authored for the building —
 * offering a switch to an empty map would be a dead end.
 */
export const useResourcesView = (): UseResourcesViewValues => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const queryParams = useSearchParams<SearchParams>();
  // Named for what the page does with it: there is something to toggle to exactly when the building
  // has a floorplan and the flag is on.
  const { isFloorplanAvailable: isToggleVisible, isFloorplanView, floorplans } = useFloorplanViewState();

  // Owned here rather than inside the map, because the floor chips render with the other controls
  // (so they pick up the page's padding) while the map is full-bleed below them.
  const { activeFloorplan, onFloorplanSelect } = useActiveFloorplan(floorplans, isFloorplanView);

  useDefaultFloorplanDate(isFloorplanView);

  const navigateToView = useCallback(
    (view?: string) => {
      // Going back to the list drops the param rather than setting a value — the list is the
      // default, so a clean url is the one without it. `qs.stringify` leaves undefined values out.
      const nextParams = { ...queryParams, [RESOURCES_VIEW_QUERY_PARAM]: view };

      dispatch(replace(`${pathname}?${qs.stringify(nextParams)}`));
    },
    [dispatch, pathname, queryParams],
  );

  const showList = useCallback(() => navigateToView(undefined), [navigateToView]);
  const showFloorplan = useCallback(() => navigateToView(FLOORPLAN_VIEW), [navigateToView]);

  return useMemo(
    () => ({
      isToggleVisible,
      isFloorplanView,
      floorplans,
      activeFloorplan,
      onFloorplanSelect,
      showList,
      showFloorplan,
    }),
    [isToggleVisible, isFloorplanView, floorplans, activeFloorplan, onFloorplanSelect, showList, showFloorplan],
  );
};
